import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';
import { DialogService } from '../../shared/services/dialog.service';

/* -------------------------------------------------------------------------- */
/*                              ANCHOR authGuard                               */
/* -------------------------------------------------------------------------- */
/** Requiere sesión. Sin sesión → /login. */
export const authGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  await auth.whenReady();
  return auth.isLoggedIn ? true : router.createUrlTree(['/login']);
};

/* -------------------------------------------------------------------------- */
/*                             ANCHOR noAuthGuard                              */
/* -------------------------------------------------------------------------- */
/** Solo sin sesión (login, registro, recuperar). Con sesión → /dashboard. */
export const noAuthGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  await auth.whenReady();
  return auth.isLoggedIn ? router.createUrlTree(['/dashboard']) : true;
};

/* -------------------------------------------------------------------------- */
/*                              ANCHOR sudoGuard                               */
/* -------------------------------------------------------------------------- */
/** Solo profile = 'sudo' (catálogos de la plataforma). */
export const sudoGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  await auth.whenReady();
  return auth.isSudo() ? true : router.createUrlTree(['/dashboard']);
};

/* -------------------------------------------------------------------------- */
/*                         ANCHOR companyAccessGuard                           */
/* -------------------------------------------------------------------------- */
/**
 * Membresía del negocio: Dueño o Administrador (owner/admin) o el sudo. La API valida el acceso en GET /companies/{idCompany};
 * si el usuario es Staff, `CompanyService.open` no abre el negocio y lo regresa a Negocios (Staff solo usa la app).
 * NO espera la red: solo arranca la petición (compartida, sin duplicados) para que la navegación sea inmediata.
 * El BusinessShell (y el Diseñador, que vive fuera de él) muestran el estado de carga mientras llega el detalle y,
 * si la API responde 403/404, avisan y regresan a Negocios (`CompanyService.enter`).
 */
export const companyAccessGuard: CanActivateFn = async route => {
  const auth = inject(AuthService);
  const companies = inject(CompanyService);
  await auth.whenReady(); // sesión local de Firebase (sin red); el token se necesita para la petición
  if (!auth.isLoggedIn) return true; // authGuard redirige a /login
  void companies.ensure(route.paramMap.get('idCompany') ?? '');
  return true;
};

/* -------------------------------------------------------------------------- */
/*                        ANCHOR unsavedChangesGuard                           */
/* -------------------------------------------------------------------------- */
export interface IHasUnsavedChanges {
  hasUnsavedChanges(): boolean;
  /** Permite navegar sin preguntar (p. ej. entre pasos del asistente). */
  allowNavigation?(url: string): boolean;
}

export const unsavedChangesGuard: CanDeactivateFn<IHasUnsavedChanges> = async (component, route, state, next) => {
  if (!component?.hasUnsavedChanges?.()) return true;
  if (next && component.allowNavigation?.(next.url)) return true;
  return inject(DialogService).confirm({
    icon: 'alert',
    title: '¿Salir sin guardar?',
    body: 'Perderás los cambios que no has guardado.',
    confirmLabel: 'Salir',
    cancelLabel: 'Seguir editando',
  });
};
